'use client';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { type Paddle } from '@/lib/services/paddleService';

interface ComparisonContextType {
  selectedItems: Paddle[];
  addItem: (paddle: Paddle) => void;
  removeItem: (id: string) => void;
  clearItems: () => void;
  isSelected: (id: string) => boolean;
  closeBuilder: () => void;
  openBuilder: () => void;
  isBuilderOpen: boolean;
}

const STORAGE_KEY = 'paddlevs_comparison_selection_v2';

const ComparisonContext = createContext<ComparisonContextType | null>(null);

export function ComparisonProvider({ children }: { children: React.ReactNode }) {
  const [selectedItems, setSelectedItems] = useState<Paddle[]>([]);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);

  // Persist to localStorage whenever selection changes
  useEffect(() => {
    try {
      // Store full paddle objects so we can restore without re-fetching
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedItems));
    } catch {
      // ignore storage errors
    }
  }, [selectedItems]);

  // Restore from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const items: Paddle[] = JSON.parse(stored);
        if (Array.isArray(items) && items.length > 0) {
          setSelectedItems(items);
        }
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  const addItem = useCallback((paddle: Paddle) => {
    setSelectedItems(prev => {
      if (prev.find(p => p.id === paddle.id)) return prev;
      if (prev.length >= 4) return prev; // max 4
      return [...prev, paddle];
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setSelectedItems(prev => prev.filter(p => p.id !== id));
  }, []);

  const clearItems = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const isSelected = useCallback((id: string) => {
    return selectedItems.some(p => p.id === id);
  }, [selectedItems]);

  const closeBuilder = useCallback(() => {
    setIsBuilderOpen(false);
  }, []);

  const openBuilder = useCallback(() => {
    setIsBuilderOpen(true);
  }, []);

  return (
    <ComparisonContext.Provider value={{ selectedItems, addItem, removeItem, clearItems, isSelected, closeBuilder, openBuilder, isBuilderOpen }}>
      {children}
    </ComparisonContext.Provider>
  );
}

export function useComparison() {
  const ctx = useContext(ComparisonContext);
  if (!ctx) throw new Error('useComparison must be used within ComparisonProvider');
  return ctx;
}
